import { localScopeStore } from "./localScopeStore.js";
import { userScopeStore } from "./userScopeStore.js";
/**
 * Create an isolated module-storage runtime over the given local `backends`. The shell
 * owns one runtime; `configure` is called once at boot, and `createContext(type)` builds
 * the {@link ModuleContext} handed to each module lifecycle. Operations issued before
 * `configure` resolve once it runs.
 */
export const createModuleStorageRuntime = (backends) => {
    let current;
    let resolveReady = () => { };
    const ready = new Promise((resolve) => {
        resolveReady = resolve;
    });
    const config = async () => {
        await ready;
        return current;
    };
    const configListeners = new Set();
    const makeStore = (scope, type) => {
        if (scope === "session")
            return localScopeStore(backends.session, type, config);
        if (scope === "browser")
            return localScopeStore(backends.browser, type, config);
        return userScopeStore(type, config, (cb) => {
            configListeners.add(cb);
        });
    };
    return {
        configure: (next) => {
            const prev = current;
            current = next;
            resolveReady(next);
            const changed = prev !== undefined && (prev.transport !== next.transport || prev.userId !== next.userId);
            if (changed)
                for (const cb of [...configListeners])
                    cb();
        },
        createContext: (type) => {
            const stores = new Map();
            return {
                storage: (scope) => {
                    let store = stores.get(scope);
                    if (!store) {
                        store = makeStore(scope, type);
                        stores.set(scope, store);
                    }
                    return store;
                },
            };
        },
    };
};
let singleton;
const singletonRuntime = () => {
    singleton ??= createModuleStorageRuntime({ session: globalThis.sessionStorage, browser: globalThis.localStorage });
    return singleton;
};
/**
 * Inject identity and the server transport once at app boot. Storage operations issued
 * earlier (e.g. from a module's `init`) wait and flush once this runs.
 */
export const configureModuleStorage = (config) => {
    singletonRuntime().configure(config);
};
/**
 * Build the {@link ModuleContext} for a module `type`. Shell-internal: called by the
 * registry for `init` and by the Pane host for `load`; modules receive the context,
 * they never construct one.
 */
export const createModuleContext = (type) => singletonRuntime().createContext(type);
//# sourceMappingURL=moduleStorage.js.map